/**
 * Notification schemas — in-app bell + push notifications.
 */

import { z } from 'zod';
import { idSchema, iso8601Schema, pageInput, booleanQuerySchema } from './common';

export const notificationTypeSchema = z.enum([
  'BOUNCE_ESCALATION',
  'COMPLIANCE_INSTANCE_ASSIGNED',
  'COMPLIANCE_INSTANCE_OVERDUE',
  'COMPLIANCE_INSTANCE_DUE_SOON',
  'COMPLIANCE_INSTANCE_OVERRIDDEN',
  'ENGAGEMENT_KICKOFF_DUE',
  'INVOICE_DUE',
  'INVOICE_PAID',
  'PROPOSAL_SIGNED',
  'PROPOSAL_NEGOTIATION',
  'PROPOSAL_DECLINED',
  'RECURRING_SERIES_CREATED',
  'RECURRING_CADENCE_CHANGED',
  'RECURRING_PAUSED',
  'RECURRING_RESUMED',
  'TIME_ENTRY_REMINDER',
  'MEETING_REMINDER',
  'MEETING_RSVP',
  'NOTE_ADDED',
  'LEAVE_REQUESTED',
  'LEAVE_DECIDED',
  'GENERIC',
]);

export const notificationOutputSchema = z.object({
  id: idSchema,
  userId: idSchema,
  type: notificationTypeSchema,
  title: z.string().max(200),
  body: z.string().max(2000),
  actionUrl: z.string().max(2048).nullable(),
  entityType: z.string().nullable(),
  entityId: idSchema.nullable(),
  readAt: iso8601Schema.nullable(),
  pushSentAt: iso8601Schema.nullable(),
  emailSentAt: iso8601Schema.nullable(),
  createdAt: iso8601Schema,
});
export type NotificationOutput = z.infer<typeof notificationOutputSchema>;

export const listNotificationsInputSchema = pageInput.extend({
  unreadOnly: booleanQuerySchema.optional(),
  type: notificationTypeSchema.optional(),
});
export type ListNotificationsInput = z.infer<typeof listNotificationsInputSchema>;

export const markReadInputSchema = z.object({
  ids: z.array(idSchema).min(1).max(100),
});

export const registerPushTokenInputSchema = z.object({
  /** Expo push token (ExponentPushToken[...] format). */
  expoPushToken: z.string().min(10).max(200),
  platform: z.enum(['ios', 'android']),
});
export type RegisterPushTokenInput = z.infer<typeof registerPushTokenInputSchema>;

/* ── Browser push (Web Push) — per-device subscription, not policy ───────── */

export const browserPushSubscribeInputSchema = z.object({
  endpoint: z.string().url().max(2048),
  keys: z.object({
    p256dh: z.string().min(1).max(255),
    auth: z.string().min(1).max(255),
  }),
  userAgent: z.string().max(255).optional(),
});
export type BrowserPushSubscribeInput = z.infer<typeof browserPushSubscribeInputSchema>;

export const browserPushUnsubscribeInputSchema = z.object({
  endpoint: z.string().url().max(2048),
});
export type BrowserPushUnsubscribeInput = z.infer<typeof browserPushUnsubscribeInputSchema>;

export const browserPushStatusInputSchema = z.object({
  endpoint: z.string().url().max(2048),
});
export type BrowserPushStatusInput = z.infer<typeof browserPushStatusInputSchema>;